import { ChangeDetectionStrategy, Component, inject } from '@angular/core';

import { ApiProtectedService } from '@app/services/api/api.protected.service';
import { ActivatedRoute, Router } from '@angular/router';
import { PortalRoutes } from '@app/app-routing.module';
import { catchError, map, Observable, of, switchMap, tap } from 'rxjs';
import { PortalBookingNode } from '@app/services/api/api.types';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Tracking } from '@app/services/tracking/tracking.service';
import { PopupsService } from '@voyagu/portal-design-system';
import { VerificationModalService } from '@app/services/verification-modal.service';
import { StateService } from '@app/services/state/state.service';
import { PortalTrackingEvents } from '@app/utils/tracking-events';

@Component({
  selector: 'booking-page',
  templateUrl: './booking-page.component.html',
  styleUrls: ['./booking-page.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BookingPageComponent {
  private apiProtectedService = inject(ApiProtectedService);
  private router = inject(Router);
  private activateRoute = inject(ActivatedRoute);
  protected verificationService = inject(VerificationModalService);
  protected popupsService = inject(PopupsService);
  protected stateService = inject(StateService);

  protected isLoading = true;

  protected booking$: Observable<PortalBookingNode | null> = this.activateRoute.paramMap.pipe(
    switchMap((data) => {
      const id = data.get(PortalRoutes.BookingsParam);

      if (id) {
        return this.apiProtectedService.portalAuthorizedBooking(id).pipe(
          tap((response) => {
            const verification = response.data?.portalAuthorizedBooking?.verification;

            if (verification?.required) {
              this.verificationService.showModal(
                verification,
                this.stateService.getState().portalLink,
              );
            }

            if (verification) {
              Tracking.patch({ verificationRequired: verification.required });
            }
          }),
          map((response) => response.data?.portalAuthorizedBooking?.booking ?? null),
          catchError(() => {
            Tracking.track(PortalTrackingEvents.portalBookingRequestError);
            return of(null);
          }),
        );
      }

      void this.router.navigate([``], { queryParamsHandling: 'merge' });

      return of(null);
    }),
    tap(() => (this.isLoading = false)),
    takeUntilDestroyed(),
  );
}
